import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../../../../components/Header/Header";
import img1 from "../Questionnaire/QuestionnairePic.png";
import api from "../../../chat/api";
import { Loader2, Plus, Folder } from "lucide-react";
import toast from "react-hot-toast";

import SelectFormProjectModal from "./SelectFormProjectModal";
import FormCard from "./FormCard";

const FormsPage = () => {
  const { projectId, orgId } = useParams();
  const navigate = useNavigate();
  const folderId = projectId;
  const [forms, setForms] = useState([]);
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);

  // Dropdown state for Header "Add" button
  const dropdownRef = useRef(null);

  // Delete State (job id currently being removed)
  const [deletingId, setDeletingId] = useState(null);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/rfp/autopdf/jobs/`);

      if (response.status === 200) {
        const rawList = response.data?.jobs || [];

        // Scope to this project's folder.
        const folderIdNum = Number(folderId);
        const scoped = rawList.filter((j) => {
          if (!folderId) return true;
          return Number(j.folder_id) === folderIdNum;
        });

        const mappedData = scoped.map((item) => ({
          id: item.job_id,
          jobId: item.job_id,
          name: item.name || "Untitled Form",
          fileType: item.name?.split(".").pop() || "pdf",
          rawDate: item.created_at ? new Date(item.created_at) : new Date(0),
          status: item.status || "-",
          mode: item.mode || null,
          pdfUrl: item.pdf_url || "",
          filledPdfUrl: item.filled_pdf_url || "",
          url: item.filled_pdf_url || item.pdf_url || "",
        }));

        const sortedData = mappedData.sort((a, b) => b.rawDate - a.rawDate);
        setForms(sortedData);
        setPermission(null);
      } else {
        setForms([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast.error("Failed to load forms.");
      setForms([]);
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleDownload = async (e, url, fileName) => {
    e.stopPropagation();

    if (!url) {
      toast.error("Download link not available.");
      return;
    }

    const name = fileName || "document";
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      // Could not fetch the file for a renamed download (e.g. CORS) —
      // fall back to opening it in a separate tab.
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (!opened) toast.error("Failed to download file.");
    }
  };

  const handleDelete = async (jobId, event) => {
    event?.stopPropagation?.();
    setDeletingId(jobId);

    try {
      // Remove the AutoPDF form job (not the underlying KB document, which is
      // shared with the rest of the project).
      const res = await api.delete(`/api/v1/rfp/autopdf/jobs/${jobId}/delete/`);

      if (res.status === 200 || res.status === 204) {
        toast.success("Form removed.");
        setForms((prev) => prev.filter((f) => f.jobId !== jobId));
      }
    } catch (error) {
      console.error("Error deleting form:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to remove form. Please try again.";
      toast.error(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const handleImportSuccess = () => {
    fetchForms();
  };

  const handleRowClick = (jobId, row) => {
    const path = orgId
      ? `/rfp-projects/project/${projectId}/org/${orgId}/forms/review/${jobId}`
      : `/rfp-projects/project/${projectId}/forms/review/${jobId}`;
    navigate(path, {
      state: {
        pdfUrl: row?.filledPdfUrl || row?.pdfUrl || "",
        name: row?.name,
      },
    });
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-[#FEF9F7]">
      {/* Navigation Header */}
      <div className="flex-shrink-0">
        <Header
          icon={"/arrow-back.svg"}
          name="Back to Project"
          onClick={() =>
            navigate(`/rfp-projects/project/${projectId}/org/${orgId}`)
          }
        />
      </div>

      {/* Main Content Container */}
      <div className="flex-1 min-h-0 w-full px-4 pt-3 pb-4 box-border">
        <div className="flex flex-col bg-white rounded-md font-outfit shadow-sm border border-gray-100 h-full min-h-0 overflow-hidden">
          {/* Internal Header Section */}
          <div className="px-8 py-5 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
            <div>
              <h1 className="text-xl !font-semibold text-[#18101C]">
                Forms Manager
              </h1>
              <p className="text-[14px] text-[#52525B] font-normal mt-1">
                Import and fill forms from your project files
              </p>
            </div>

            {/* Show Add button in header only if we have data */}
            {!loading && forms.length > 0 && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProjectModalOpen(true)}
                  className="flex items-center gap-2 bg-[#C95EBE] text-white px-4 py-2 rounded-lg text-[14px] font-medium transition-colors"
                >
                  <Plus size={18} />
                  Add Form
                </button>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col p-3">
            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-[#52525B]">
                <Loader2 className="w-8 h-8 animate-spin text-[#C95EBE]" />
                <span className="font-medium">Fetching Forms...</span>
              </div>
            ) : forms.length > 0 ? (
              <div className="flex-1 min-h-0 overflow-auto p-2">
                <div
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(180px, 1fr))",
                  }}
                >
                  {forms.map((item) => (
                    <FormCard
                      key={item.id}
                      item={item}
                      deleting={deletingId === item.jobId}
                      onOpen={(it) => handleRowClick(it.jobId, it)}
                      onDownload={handleDownload}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* Empty State - Only Import Option */
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div className="mb-6">
                  <img
                    src={img1}
                    alt="No forms"
                    className="w-64 h-auto object-contain"
                  />
                </div>

                <h2 className="text-lg font-semibold text-[#18101C] mb-2">
                  No Forms Found!
                </h2>
                <p className="text-[#52525B] font-normal text-center mb-8 max-w-lg">
                  Import form files from your project to start filling them
                </p>

                {/* Action Buttons - Only Import option */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setIsProjectModalOpen(true)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C95EBE] text-white font-medium hover:bg-[#b04da6] transition-colors shadow-sm"
                  >
                    <Folder size={18} />
                    Import from Project
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Select Modal (autopdf process → fill → finalize → download) */}
      <SelectFormProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        folderId={folderId}
        onImport={handleImportSuccess}
      />
    </div>
  );
};

export default FormsPage;
